import React, { useState, createRef } from 'react';
import { Box, Input, Select, useBreakpointIndex } from '@apia/theme';
import { getLabel, noNaN } from '@apia/util';
import { TTooltip, ApiaUtil } from '../../objects';
import { IconButton, SimpleButton } from '../forms';

const defaultLabels = Object.freeze({
  totalRecords: window.GNR_TOT_RECORDS,
  totalRecordsReached: window.GNR_TOT_RECORDS_REACHED,
  maximize: window.LBL_MAXIMIZE,
  minimize: window.LBL_MINIMIZE,
  information: window.BTN_FILE_INFO_TOOLTIP,
  deleteFilters: window.BTN_DELETE_FILTERS,
  refresh: window.NAV_REFRESH,
  firstPage: window.NAV_FIRST_PAGE,
  previousPage: window.NAV_PREVIOUS_PAGE,
  lastPage: window.NAV_LAST_PAGE,
  nextPage: window.NAV_NEXT_PAGE,
  pageInputInstructions: window.NAV_INPUT_LABEL,
  page: window.PAGE,
  moreData: window.QUERY_MORE_DATA,
});

export interface IPagination {
  appliedFilters?: number;
  /**
   * Este valor determina si el boton de eliminar filtros debe mostrarse como si hubieran filtros sin aplicar (en caso de que haya filtros aplicados por defecto) o si debe mostrarse como si todos los filtros con valor estan aplicados a la busqueda actual. Se deberia verificar con el slice de redux correspondiente a la tabla.
   */
  areAllFiltersApplied?: boolean;

  className?: string;
  currentPage: number | string;
  disabled?: boolean;
  disableReduced?: boolean;
  hideInfo?: boolean;
  hideMaximizeButton?: boolean;
  hideRefreshButton?: boolean;
  isLoading?: boolean;
  /**
   * Valor obtenido del buttonsSlice para bloquear los botones de la paginacion mientras se estan ejecutando acciones
   */
  isPerforming?: boolean;
  /**
   * Si se pasa un método onDeleteFilters, aparece el botón "Eliminar filtros"
   * cerca del refresh
   */
  onDeleteFilters?: () => unknown;
  onPageChange: (page: number) => unknown;
  onRefresh: (page: number) => unknown;
  pageCount: number | string;
  reachedMax?: boolean | undefined;
  hasMore?: boolean | undefined;
  /**
   * Si la cantidad de registros se obtiene desde un lugar distinto de Redux,
   * se puede pasar como number directamente
   */
  recordsCount?: number;
  showMaximizeOnSmallBreakpoints?: boolean;
  variant?: 'primary' | 'secondary' | 'datagrid';

  onMaximize?: () => unknown;
  isMaximized?: boolean;
  labels?: Partial<typeof defaultLabels>;
}

const NoMemoPagination = ({
  appliedFilters,
  areAllFiltersApplied,
  className,
  currentPage,
  disabled,
  disableReduced,
  hasMore,
  hideInfo,
  hideMaximizeButton,
  hideRefreshButton,
  isLoading,
  isPerforming,
  onDeleteFilters,
  onPageChange,
  onRefresh,
  pageCount,
  recordsCount: outerRecordsCount,
  reachedMax,
  showMaximizeOnSmallBreakpoints,
  variant = 'primary',
  isMaximized,
  onMaximize,
  labels,
}: IPagination) => {
  const numberPageCount = Number(pageCount);
  const numberCurrentPage = Number(currentPage);
  const breakPoint = useBreakpointIndex();
  const recordsCount = outerRecordsCount;

  const definedLabels = Object.assign({}, defaultLabels, labels);

  const [hasNoValue, setHasNoValue] = useState<boolean>(false);

  const InputRef = createRef<HTMLInputElement>();

  const options = [];
  for (let i = 1; i <= numberPageCount; i++) {
    options.push(i);
  }

  const [infoButtonRef, setInfoButtonRef] = React.useState<HTMLElement | null>(
    null,
  );

  const recordsCountLabel = `${definedLabels.totalRecords}: ${recordsCount}`;
  const tooltipProps: TTooltip = React.useMemo(
    () => ({
      text:
        breakPoint <= 3
          ? `${recordsCountLabel}.${
              !reachedMax && !hasMore
                ? ''
                : (reachedMax && definedLabels.totalRecordsReached) ||
                  (hasMore && definedLabels.moreData)
            }`
          : `${
              !reachedMax && !hasMore
                ? ''
                : (reachedMax && definedLabels.totalRecordsReached) ||
                  (hasMore && definedLabels.moreData)
            }`,
      attachToElement: { current: infoButtonRef! },
      closeOnClickOut: true,
    }),
    [
      breakPoint,
      definedLabels.moreData,
      definedLabels.totalRecordsReached,
      hasMore,
      infoButtonRef,
      reachedMax,
      recordsCountLabel,
    ],
  );
  return (
    <Box
      as="nav"
      variant={`layout.common.components.pagination.${variant}`}
      aria-label={getLabel('lblQryParamPag').text}
      className={`pagination ${
        numberPageCount <= 1 && !disableReduced ? 'onlyRefresh' : ''
      } ${className ?? ''}`}
    >
      <Box className="pagination__leftToolbar">
        {!hideMaximizeButton &&
          (showMaximizeOnSmallBreakpoints || breakPoint > 4) && (
            <IconButton
              variant={'icon'}
              className="pagination__fullScreenButton"
              onClick={onMaximize}
              icon={!isMaximized ? 'Maximize' : 'Minimize'}
              title={
                !isMaximized ? definedLabels.maximize : definedLabels.minimize
              }
              aria-label={
                !isMaximized ? definedLabels.maximize : definedLabels.minimize
              }
              iconSize={'Md'}
            />
          )}
        {!hideInfo && (
          <>
            {(((reachedMax || hasMore) && breakPoint > 3) || breakPoint <= 3) &&
            className !== 'grid__pagination' ? (
              <IconButton
                variant={'icon-primary'}
                icon="Info"
                className={`pagination__information ${
                  reachedMax || hasMore ? 'pagination__alertInfo' : ''
                }`}
                onClick={() => ApiaUtil.instance.tooltips.open(tooltipProps)}
                ref={setInfoButtonRef}
                iconSize={'Md'}
                aria-label={definedLabels.information}
                title={definedLabels.information}
              />
            ) : (
              ''
            )}
            {breakPoint > 3 && (
              <Box
                as="span"
                className="recordsCounter pagination__deleteLabels"
                title={recordsCountLabel}
              >
                {recordsCount !== -1 &&
                  `${definedLabels.totalRecords}: ${recordsCount} `}
              </Box>
            )}
          </>
        )}
      </Box>

      <Box className="pagination__controls">
        {(numberPageCount > 0 || disableReduced) && (
          <>
            {breakPoint > 2 && (
              <IconButton
                variant={'icon-primary'}
                disabled={
                  disabled ||
                  numberCurrentPage === 1 ||
                  isLoading ||
                  isPerforming
                }
                title={definedLabels.firstPage}
                aria-label={definedLabels.firstPage}
                type="button"
                onClick={() => {
                  onPageChange(1);
                }}
                icon="First"
                iconSize={'Sm'}
              />
            )}

            <IconButton
              variant={'icon-primary'}
              disabled={
                disabled || numberCurrentPage <= 1 || isLoading || isPerforming
              }
              title={definedLabels.previousPage}
              aria-label={definedLabels.previousPage}
              type="button"
              onClick={() => {
                onPageChange(numberCurrentPage - 1);
              }}
              icon="Previous"
              iconSize={'Sm'}
            />
            {breakPoint > 2 && (
              <Box className="input">
                <Input
                  className="pagination__controls__input"
                  aria-label={definedLabels.pageInputInstructions}
                  title={`${definedLabels.page}: ${numberCurrentPage} `}
                  ref={InputRef}
                  value={hasNoValue ? '' : numberCurrentPage}
                  onKeyDown={(ev) => {
                    const regex = /([0-9])|(Backspace)|(Tab)/;
                    if (!ev.key.match(regex)) {
                      ev.stopPropagation();
                      ev.preventDefault();
                    }
                    if (ev.key === 'Enter') {
                      ev.stopPropagation();
                      ev.preventDefault();
                      onPageChange(numberCurrentPage);
                    }
                  }}
                  onBlur={() => {
                    onPageChange(numberCurrentPage);
                  }}
                  onFocus={(ev) => {
                    ev.currentTarget.select();
                  }}
                  onChange={(ev) => {
                    setHasNoValue(ev.target.value === '');
                    if (ev.target.value !== '') {
                      onPageChange(
                        Math.max(
                          1,
                          Math.min(
                            noNaN(Number(ev.currentTarget.value)),
                            pageCount as number,
                          ),
                        ),
                      );
                    }
                  }}
                  disabled={disabled || isLoading || isPerforming}
                />{' '}
                /{' '}
                <Box
                  as="span"
                  title={
                    recordsCount !== -1
                      ? `${definedLabels.totalRecords} ${recordsCount}`
                      : definedLabels.pageInputInstructions
                  }
                >
                  {pageCount}
                </Box>
              </Box>
            )}
            {breakPoint <= 2 && (
              <Select
                className="pagination__selectPage"
                // menuplacement="top"
                value={numberCurrentPage}
                onChange={(e) => {
                  onPageChange(Number(e.target.value));
                }}
              >
                <option disabled>Total: {numberPageCount}</option>
                {options.map((current) => {
                  return <option key={current}>{current}</option>;
                })}
              </Select>
            )}
            <IconButton
              variant={'icon-primary'}
              disabled={
                disabled ||
                numberCurrentPage >= numberPageCount ||
                isLoading ||
                isPerforming
              }
              title={definedLabels.nextPage}
              aria-label={definedLabels.nextPage}
              type="button"
              onClick={() => {
                onPageChange(numberCurrentPage + 1);
              }}
              icon="Next"
              iconSize={'Sm'}
            />

            {breakPoint > 2 && (
              <IconButton
                variant={'icon-primary'}
                type="button"
                title={definedLabels.lastPage}
                aria-label={definedLabels.lastPage}
                disabled={
                  disabled ||
                  numberCurrentPage >= numberPageCount ||
                  isLoading ||
                  isPerforming
                }
                onClick={() => {
                  onPageChange(numberPageCount);
                }}
                icon="Last"
                iconSize={'Sm'}
              />
            )}
          </>
        )}
      </Box>
      <Box className="pagination__rightToolbar">
        {(!hideRefreshButton || onDeleteFilters) && (
          <>
            {onDeleteFilters && (
              <SimpleButton
                disabled={isPerforming}
                className="pagination__deleteFiltersButton"
                data-test-id="btn-clear-filters"
                onClick={onDeleteFilters}
              >
                {breakPoint > 3 && (
                  <Box as="span" className="pagination__deleteLabels">
                    {definedLabels.deleteFilters}
                  </Box>
                )}
                <Box
                  as="span"
                  className={`pagination__appliedFiltersLabel ${
                    areAllFiltersApplied ? '' : 'withFilters'
                  }`}
                >
                  {appliedFilters}
                </Box>
              </SimpleButton>
            )}
            {!hideRefreshButton && (
              <IconButton
                title={definedLabels.refresh}
                aria-label={definedLabels.refresh}
                onClick={() => onRefresh(numberCurrentPage)}
                icon="Refresh"
                iconSize={'Md'}
                disabled={disabled || isLoading}
                variant={'icon-primary'}
                isLoading={isPerforming}
                className="pagiantion__refreshIcon"
                data-test-id="btn-refresh"
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export const Pagination = React.memo(NoMemoPagination);
